// Plan 08-08 — PixiJS Ticker motion gate.
//
// Pitfall 5 lock (RESEARCH.md):
//   `app.ticker.speed = 0` does NOT pause the Ticker — deltaTime scales to
//   zero but the RAF loop keeps firing, and any code that uses elapsedMS
//   instead of deltaTime keeps running. Setting speed to 0 also silently
//   breaks tweens that rely on deltaTime accumulation: animations freeze
//   mid-frame and never resume cleanly when speed is restored.
//
// Decision (per UI-SPEC §"Reduced-motion substitution table"):
//   Ticker.speed STAYS at 1.0 unconditionally. Reduced-motion behaviour is
//   gated TWO layers up:
//     - tween-config.ts → selectEasing/selectDuration substitute curves
//     - particles.ts    → ParticleEmitter.emit({reduced:true}) returns []
//   Cinematic reveals (T1/T6/T12) collapse to a single 200ms cross-fade by
//   passing `dur-cinematic-long` through `selectDuration(token, true)` →
//   `dur-quick` (200ms). The Ticker itself sees no change.
//
// Why keep this hook at all? Plan 04's `App.tsx` calls
// `useTickerMotionGate?.()` from inside `<Application>` so the call-site
// is wired up. Future work (e.g., half-speed cinematics, frame-budget
// throttling on low-end mobile) can land in this hook without touching
// Plan 04's call-site. Today: documented no-op.
//
// THIS HOOK MUST NEVER:
//   - set ticker.speed = 0 (Pitfall 5)
//   - clamp ticker.maxFPS below 60 (degrades all animations, not just cinematics)
//   - destroy or stop the Ticker (breaks the WebGL/WebGPU render loop)

/**
 * No-op motion gate. The reduced-motion contract is enforced at the tween
 * config + particle emitter layer (see tween-config.ts + particles.ts).
 *
 * Kept as a function (not an arrow constant) so the optional-chain call
 * site `useTickerMotionGate?.()` in `apps/web/src/game/App.tsx` resolves
 * to a real function expression, and future implementations drop in via
 * file-replacement.
 */
export function useTickerMotionGate(): void {
  // Intentionally empty — see header comment for the Pitfall 5 rationale.
}
